import React, { useEffect, useRef } from 'react'

const SCROLL_TO_BOTTOM_EVENT = 'scrollToBottom'

export function triggerScrollToBottom() {
	window.dispatchEvent(new CustomEvent(SCROLL_TO_BOTTOM_EVENT))
}

export function useBottomScrolling(
	containerRef: React.RefObject<HTMLElement | null>,
	enabled: boolean,
	deps: unknown[] = []
) {
	const lastScrolledHeightRef = useRef<number>(0)

	useEffect(() => {
		if (!enabled) return
		const element = containerRef.current
		if (!element) return

		const scrollToBottomIfGrown = () => {
			const currentHeight = element.scrollHeight
			if (currentHeight > lastScrolledHeightRef.current) {
				element.scrollTo({ top: currentHeight })
				lastScrolledHeightRef.current = currentHeight
			}
		}

		const timeoutIds: number[] = []
		const rafId = requestAnimationFrame(() => {
			scrollToBottomIfGrown()
			timeoutIds.push(window.setTimeout(scrollToBottomIfGrown, 150))
			timeoutIds.push(window.setTimeout(scrollToBottomIfGrown, 400))
		})

		return () => {
			cancelAnimationFrame(rafId)
			timeoutIds.forEach(id => clearTimeout(id))
		}
	}, [enabled, containerRef, ...deps])

	useEffect(() => {
		if (!enabled) return
		const element = containerRef.current
		if (!element) return

		const handleImageLoad = () => {
			const currentHeight = element.scrollHeight
			if (currentHeight > lastScrolledHeightRef.current) {
				element.scrollTo({ top: currentHeight })
				lastScrolledHeightRef.current = currentHeight
			}
		}

		const images = Array.from(element.querySelectorAll('img'))
		images.forEach(img => {
			if (!img.complete) {
				img.addEventListener('load', handleImageLoad)
			}
		})

		return () => {
			images.forEach(img => img.removeEventListener('load', handleImageLoad))
		}
	}, [enabled, containerRef, ...deps])

	const scrollToBottom = React.useCallback(() => {
		if (!enabled) return
		const element = containerRef.current
		if (!element) return

		const currentHeight = element.scrollHeight
		element.scrollTo({ top: currentHeight })
		lastScrolledHeightRef.current = currentHeight
	}, [enabled, containerRef])

	useEffect(() => {
		if (!enabled) return

		const handleScrollEvent = () => {
			scrollToBottom()
		}

		window.addEventListener(SCROLL_TO_BOTTOM_EVENT, handleScrollEvent)

		return () => {
			window.removeEventListener(SCROLL_TO_BOTTOM_EVENT, handleScrollEvent)
		}
	}, [enabled, scrollToBottom])

	return scrollToBottom
}